import { Boundaries } from "@planzone/core";
import {
  Column,
  Id,
  Identifiable,
  KanbanTask,
  Tag,
  Task,
  kanban,
} from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { reduceTask } from "../../common/task";
import { reduceTaskStatus } from "../../common/taskStatus";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { calculateAdvancement } from "../../utils/calculateAdvancement";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function sortByPercentageOfCompletion(columns: Column[]): Column[] {
  return [...columns].sort((current, next) =>
    (current.status.percentComplete || 0) < (next.status.percentComplete || 0)
      ? -1
      : 1,
  );
}

function mapColumns(
  state: State,
  map: (columns: Column[], req: State[number]) => Column[],
): State {
  return state.map((req) => ({
    ...req,
    kanban: { ...req.kanban, columns: map(req.kanban.columns, req) },
  }));
}

function mapTasks(
  state: State,
  map: (tasks: KanbanTask[]) => KanbanTask[],
): State {
  return mapColumns(state, (columns) =>
    columns.map((column) => ({ ...column, tasks: map(column.tasks) })),
  );
}

function addTask(tasks: KanbanTask[], task: KanbanTask): KanbanTask[] {
  return [...tasks, task];
}

function replaceTask(tasks: KanbanTask[], task: KanbanTask): KanbanTask[] {
  const index = tasks.findIndex(({ uuid }) => uuid === task.uuid);

  return index >= 0
    ? [
        ...tasks.slice(0, index),
        { ...tasks[index], ...task },
        ...tasks.slice(index + 1),
      ]
    : [...tasks, task];
}

function removeTask(
  tasks: KanbanTask[],
  { uuid }: Identifiable<Task>,
): KanbanTask[] {
  return tasks.filter((task) => task.uuid !== uuid);
}

function insertTaskAfter(
  tasks: KanbanTask[],
  task: KanbanTask,
  anchor: { uuid: Id } | null,
): KanbanTask[] {
  const others = tasks.filter(({ uuid }) => uuid !== task.uuid);

  if (!anchor) {
    return [task, ...others];
  }

  const index = others.findIndex(({ uuid }) => uuid === anchor.uuid);

  return index >= 0
    ? [...others.slice(0, index + 1), task, ...others.slice(index + 1)]
    : [...others, task];
}

function handleRequest(
  state: State,
  project: Boundaries["retrieveKanban"]["request"],
): State {
  const index = state.findIndex((res) => project.uuid === res.project.uuid);

  return index >= 0
    ? [
        ...state.slice(0, index),
        { project, request: setRequestStateToPending(), kanban },
        ...state.slice(index + 1),
      ]
    : [...state, { project, request: setRequestStateToPending(), kanban }];
}

function handleSuccess(
  state: State,
  { project, kanban }: Boundaries["retrieveKanban"]["success"],
): State {
  return state.map((item) =>
    item.project.uuid === project.uuid
      ? {
          ...item,
          kanban: {
            ...kanban,
            columns: sortByPercentageOfCompletion(kanban.columns).map(
              (column) => ({
                ...column,
                tasks: column.tasks.map((task) => ({
                  ...task,
                  ...calculateAdvancement(task),
                })),
              }),
            ),
          },
          request: setRequestStateToSuccess(),
        }
      : item,
  );
}

function handleFailure(
  state: State,
  project: Boundaries["retrieveKanban"]["failure"],
): State {
  return state.map((item) =>
    item.project.uuid === project.uuid
      ? { ...item, request: setRequestStateToFailure() }
      : item,
  );
}

function handleTaskRemoval(
  state: State,
  task: Boundaries["removeTask"]["success"],
): State {
  return mapTasks(state, (tasks) => removeTask(tasks, task));
}

function handleTaskCreationSuccess(
  state: State,
  { task, list, project }: Boundaries["createTask"]["success"],
): State {
  return mapColumns(state, (columns, { project: p }) =>
    columns.map((column) => {
      return task.status.uuid === column.status.uuid && p.uuid === project.uuid
        ? { ...column, tasks: addTask(column.tasks, { ...task, list }) }
        : column;
    }),
  );
}

function handleKanbanTaskCreationSuccess(
  state: State,
  { task, list, project, range }: Boundaries["createKanbanTask"]["success"],
): State {
  return mapColumns(state, (columns, { project: p }) =>
    columns.map((column) =>
      task.status.uuid === column.status.uuid && p.uuid === project.uuid
        ? {
            ...column,
            tasks: insertTaskAfter(
              column.tasks,
              { ...task, list },
              range ? range.min : null,
            ),
          }
        : column,
    ),
  );
}

function replaceTags(tasks: KanbanTask[], taskId: Id, tag: Tag): KanbanTask[] {
  const index = tasks.findIndex(({ uuid }) => uuid === taskId);

  return index >= 0
    ? [
        ...tasks.slice(0, index),
        { ...tasks[index], tags: [...tasks[index].tags, tag] },
        ...tasks.slice(index + 1),
      ]
    : tasks;
}

function removeTag(tasks: KanbanTask[], taskId: Id, tag: Tag): KanbanTask[] {
  const index = tasks.findIndex(({ uuid }) => uuid === taskId);

  return index >= 0
    ? [
        ...tasks.slice(0, index),
        {
          ...tasks[index],
          tags: tasks[index].tags.filter(
            (t) =>
              (typeof t === "object" ? t.name : t) !==
              (typeof tag === "object" ? tag.name : tag),
          ),
        },
        ...tasks.slice(index + 1),
      ]
    : tasks;
}

function handleTaskEditionRequest(
  state: State,
  { task, nextTask }: Boundaries["editTask"]["request"],
): State {
  return mapColumns(state, (columns) =>
    columns.map((column) => ({
      ...column,
      tasks:
        column.status.uuid === nextTask.status.uuid
          ? replaceTask(
              column.tasks,
              // @ts-ignore
              { ...task, ...nextTask },
            )
          : removeTask(column.tasks, task),
    })),
  );
}

function handleTaskEditionSuccess(
  state: State,
  task: Boundaries["editTask"]["success"],
): State {
  return mapColumns(state, (columns) =>
    columns.map((column) => ({
      ...column,
      tasks:
        column.status.uuid === task.status.uuid
          ? replaceTask(column.tasks, task)
          : removeTask(column.tasks, task),
    })),
  );
}

function handleMarkTaskAsCompletedSuccess(
  state: State,
  { task }: Boundaries["markTaskAsCompleted"]["success"],
): State {
  const kanbanTask = state
    .map(({ kanban }) => kanban.columns)
    .reduce((acc, columns) => [...acc, ...columns], [])
    .reduce<KanbanTask[]>((acc, { tasks }) => [...acc, ...tasks], [])
    .find(({ uuid }) => uuid === task.uuid);

  return mapColumns(state, (columns) =>
    columns.map((column) => ({
      ...column,
      tasks:
        kanbanTask && column.status.uuid === kanbanTask.status.uuid
          ? removeTask(column.tasks, task)
          : !!kanbanTask
            ? column.status.label === "pr_done"
              ? addTask(column.tasks, kanbanTask)
              : column.tasks
            : column.tasks,
    })),
  );
}

function handleMoveKanbanTaskSuccess(
  state: State,
  { task, nextColumn, range }: Boundaries["moveKanbanTask"]["success"],
): State {
  return mapColumns(state, (columns) => {
    const hasTask = !!columns.find(
      ({ tasks }) => !!tasks.find(({ uuid }) => uuid === task.uuid),
    );

    return hasTask
      ? columns.map((column) => ({
          ...column,
          tasks:
            nextColumn.status.uuid === column.status.uuid
              ? insertTaskAfter(
                  column.tasks,
                  {
                    ...task,
                    ...calculateAdvancement({
                      ...task,
                      status: nextColumn.status,
                    }),
                  },
                  range.min,
                )
              : removeTask(column.tasks, task),
        }))
      : columns;
  });
}

function handleTaskEditionFailure(
  state: State,
  { task }: Boundaries["editTask"]["failure"],
): State {
  return mapColumns(state, (columns) =>
    columns.map((column) => ({
      ...column,
      tasks:
        column.status.uuid === task.status.uuid
          ? // @ts-ignore
            replaceTask(column.tasks, task)
          : removeTask(column.tasks, task),
    })),
  );
}

function handleResourceAllocationRequest(
  state: State,
  { task, allocations }: Boundaries["allocateResources"]["request"],
): State {
  return mapTasks(state, (tasks) =>
    tasks.map((t) =>
      t.uuid === task.uuid
        ? {
            ...t,
            ...calculateAdvancement({
              ...t,
              allocations: [
                ...t.allocations,
                ...allocations.map((allocation) => ({
                  ...allocation,
                  uuid: allocation.resource.uuid,
                })),
              ],
            }),
          }
        : t,
    ),
  );
}

function handleResourceAllocationSuccess(
  state: State,
  { task, allocations }: Boundaries["allocateResources"]["success"],
): State {
  return mapTasks(state, (tasks) =>
    tasks.map((t) =>
      t.uuid === task.uuid
        ? {
            ...t,
            allocations: t.allocations.map(
              (allocation) =>
                allocations.find(
                  ({ resource }) => resource.uuid === allocation.uuid,
                ) ?? allocation,
            ),
          }
        : t,
    ),
  );
}

function handleResourceAllocationFailure(
  state: State,
  { task, allocations }: Boundaries["allocateResources"]["failure"],
): State {
  return mapTasks(state, (tasks) =>
    tasks.map((t) =>
      t.uuid === task.uuid
        ? {
            ...t,
            ...calculateAdvancement({
              ...t,
              allocations: t.allocations.filter(
                (allocation) =>
                  !allocations.find(
                    ({ resource }) => resource.uuid === allocation.uuid,
                  ),
              ),
            }),
          }
        : t,
    ),
  );
}

function handleAssignTagRequest(
  state: State,
  { assignableId, tag }: Boundaries["assignTag"]["request"],
): State {
  return mapTasks(state, (tasks) => replaceTags(tasks, assignableId, tag));
}

function handleAssignTagFailure(
  state: State,
  { assignableId, tag }: Boundaries["assignTag"]["failure"],
): State {
  return mapTasks(state, (tasks) => removeTag(tasks, assignableId, tag));
}

function handleRemoveTagRequest(
  state: State,
  { removableId, tag }: Boundaries["removeTag"]["request"],
): State {
  return mapTasks(state, (tasks) => removeTag(tasks, removableId, tag));
}

function handleRemoveTagFailure(
  state: State,
  { removableId, tag }: Boundaries["removeTag"]["failure"],
): State {
  return mapTasks(state, (tasks) => replaceTags(tasks, removableId, tag));
}

function handleEditAllocation(
  state: State,
  nextAllocation: Boundaries["editAllocation"]["success"],
): State {
  return mapTasks(state, (tasks) =>
    tasks.map((task) => ({
      ...task,
      ...calculateAdvancement({
        ...task,
        allocations: task.allocations.map((allocation) =>
          allocation.uuid === nextAllocation.uuid
            ? { ...allocation, ...nextAllocation }
            : allocation,
        ),
      }),
    })),
  );
}

function handleRemoveAllocation(
  state: State,
  { uuid }: Boundaries["editAllocation"]["success"],
): State {
  return mapTasks(state, (tasks) =>
    tasks.map((task) => ({
      ...task,
      ...calculateAdvancement({
        ...task,
        allocations: task.allocations.filter(
          (allocation) => allocation.uuid !== uuid,
        ),
      }),
    })),
  );
}

function handleStatusCreationRequest(
  state: State,
  { status }: Boundaries["createTaskStatus"]["request"],
): State {
  return mapColumns(state, (columns) =>
    sortByPercentageOfCompletion([
      ...columns,
      { status: { ...status, uuid: status.label }, tasks: [] },
    ]),
  );
}

function handleStatusCreationSuccess(
  state: State,
  { status }: Boundaries["createTaskStatus"]["success"],
): State {
  return mapColumns(state, (columns) =>
    sortByPercentageOfCompletion(
      columns.map((column) =>
        column.status.uuid === status.label ? { ...column, status } : column,
      ),
    ),
  );
}

function handleStatusCreationFailure(
  state: State,
  { status }: Boundaries["createTaskStatus"]["failure"],
): State {
  return mapColumns(state, (columns) =>
    sortByPercentageOfCompletion(
      columns.filter((column) => column.status.uuid !== status.label),
    ),
  );
}

function handleStatusRemoval(
  state: State,
  { status, nextStatus }: Boundaries["removeTaskStatus"]["success"],
): State {
  return mapColumns(
    mapColumns(state, (columns) =>
      columns.map((column) => {
        const removedCol = columns.find(
          (col) => col.status.uuid === status.uuid,
        );

        return column.status.uuid === nextStatus.uuid && removedCol
          ? {
              ...column,
              tasks: [
                ...column.tasks,
                ...removedCol.tasks.map((task) => ({
                  ...task,
                  ...calculateAdvancement({ ...task, status: column.status }),
                })),
              ],
            }
          : column;
      }),
    ),
    (columns) => columns.filter((column) => column.status.uuid !== status.uuid),
  );
}

function handleEditTaskStatusRequest(
  state: State,
  { status, nextStatus }: Boundaries["editTaskStatus"]["request"],
): State {
  return mapColumns(state, (columns) =>
    columns.map((column) =>
      column.status.uuid === status.uuid
        ? {
            ...column,
            status: { uuid: status.uuid, ...nextStatus },
            tasks: column.tasks.map((task) => ({
              ...task,
              ...calculateAdvancement({
                ...task,
                status: { uuid: status.uuid, ...nextStatus },
              }),
            })),
          }
        : column,
    ),
  );
}

function handleEditTaskStatusSuccess(
  state: State,
  nextStatus: Boundaries["editTaskStatus"]["success"],
): State {
  return mapColumns(state, (columns) =>
    columns.map((column) =>
      column.status.uuid === nextStatus.uuid
        ? {
            ...column,
            status: { ...column.status, ...nextStatus },
            tasks: column.tasks.map((task) => ({
              ...task,
              ...calculateAdvancement({
                ...task,
                status: { ...column.status, ...nextStatus },
              }),
            })),
          }
        : column,
    ),
  );
}

function handleEditTaskStatusFailure(
  state: State,
  { status }: Boundaries["editTaskStatus"]["failure"],
): State {
  return mapColumns(state, (columns) =>
    columns.map((column) =>
      column.status.uuid === status.uuid
        ? {
            ...column,
            status: { ...column.status, ...status },
            tasks: column.tasks.map((task) => ({
              ...task,
              ...calculateAdvancement({
                ...task,
                status: { ...column.status, ...status },
              }),
            })),
          }
        : column,
    ),
  );
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    kanban: {
      ...req.kanban,
      columns: req.kanban.columns.map((column) => ({
        ...column,
        status: reduceTaskStatus(column.status, event),
        tasks: column.tasks.map((task) => ({
          ...task,
          ...reduceTask(task, event),
        })),
      })),
    },
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveKanbanRequested]: handleRequest,
      [events.retrieveKanbanSucceeded]: handleSuccess,
      [events.retrieveKanbanFailed]: handleFailure,
      [events.removeTaskSucceeded]: handleTaskRemoval,
      [events.createTaskSucceeded]: handleTaskCreationSuccess,
      [events.createKanbanTaskSucceeded]: handleKanbanTaskCreationSuccess,
      [events.editTaskRequested]: handleTaskEditionRequest,
      [events.editTaskSucceeded]: handleTaskEditionSuccess,
      [events.editTaskFailed]: handleTaskEditionFailure,
      [events.moveKanbanTaskSucceeded]: handleMoveKanbanTaskSuccess,
      [events.allocateResourcesRequested]: handleResourceAllocationRequest,
      [events.allocateResourcesSucceeded]: handleResourceAllocationSuccess,
      [events.allocateResourcesFailed]: handleResourceAllocationFailure,
      [events.assignTagRequested]: handleAssignTagRequest,
      [events.assignTagFailed]: handleAssignTagFailure,
      [events.removeTagRequested]: handleRemoveTagRequest,
      [events.removeTagFailed]: handleRemoveTagFailure,
      [events.editAllocationSucceeded]: handleEditAllocation,
      [events.createTaskStatusRequested]: handleStatusCreationRequest,
      [events.createTaskStatusSucceeded]: handleStatusCreationSuccess,
      [events.createTaskStatusFailed]: handleStatusCreationFailure,
      [events.removeAllocationSucceeded]: handleRemoveAllocation,
      [events.removeTaskStatusSucceeded]: handleStatusRemoval,
      [events.markTaskAsCompletedSucceeded]: handleMarkTaskAsCompletedSuccess,
      [events.editTaskStatusRequested]: handleEditTaskStatusRequest,
      [events.editTaskStatusSucceeded]: handleEditTaskStatusSuccess,
      [events.editTaskStatusFailed]: handleEditTaskStatusFailure,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
